import { useEffect, useState } from "react";
import {
  Link,
  useLocation,
} from "react-router-dom";
import {
  motion,
  AnimatePresence,
} from "framer-motion";
import { Cookie } from "lucide-react";

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const location = useLocation();

  /* =========================================================
     LANGUAGE
  ========================================================= */

  const isEnglish =
    location.pathname.startsWith("/en/");

  const language = isEnglish ? "en" : "fr";

  /* =========================================================
     LEGAL ROUTE
  ========================================================= */

  const legalPath = isEnglish
    ? "/en/legal-notice"
    : "/fr/mentions-legales";

  /* =========================================================
     TRANSLATIONS
  ========================================================= */

  const t = isEnglish
    ? {
        message:
          "We only use technical cookies necessary for the proper functioning of the website. No advertising trackers.",

        learnMore:
          "Learn more",

        accept:
          "Accept",

        decline:
          "Decline",
      }
    : {
        message:
          "Nous utilisons uniquement des cookies techniques nécessaires au bon fonctionnement du site. Aucun traceur publicitaire.",

        learnMore:
          "En savoir plus",

        accept:
          "Accepter",

        decline:
          "Refuser",
      };

  /* =========================================================
     SHOW BANNER
  ========================================================= */

  useEffect(() => {
    if (!localStorage.getItem("hc_cookie_consent")) {
      const timer = setTimeout(() => {
        setShow(true);
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, []);

  /* =========================================================
     CONSENT
  ========================================================= */

  const decide = (value) => {
    localStorage.setItem(
      "hc_cookie_consent",
      value
    );

    setShow(false);
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="cookie-banner"
          initial={{
            opacity: 0,
            y: 40,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          exit={{
            opacity: 0,
            y: 40,
          }}
          transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[200] bg-[#0a0a0a] border border-white/15 p-6"
        >
          {/* =====================================================
              MESSAGE
          ===================================================== */}

          <div className="flex items-start gap-3 mb-4">

            <Cookie
              size={18}
              className="text-acid mt-0.5 shrink-0"
            />

            <p className="font-sans font-light text-sm text-zinc-300 leading-relaxed">
              {t.message}{" "}

              <Link
                to={legalPath}
                className="text-acid hover:text-white transition-colors underline underline-offset-2"
              >
                {t.learnMore}
              </Link>
            </p>

          </div>

          {/* =====================================================
              BUTTONS
          ===================================================== */}

          <div className="flex gap-3">

            <button
              onClick={() => decide("accepted")}
              data-testid="cookie-accept"
              className="flex-1 bg-acid text-black py-3 font-mono-tech text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:bg-white"
            >
              {t.accept}
            </button>

            <button
              onClick={() => decide("declined")}
              data-testid="cookie-decline"
              className="flex-1 border border-white/20 text-white py-3 font-mono-tech text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-acid hover:text-acid"
            >
              {t.decline}
            </button>

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}